import { Link } from 'react-router-dom'
import { SEO } from '@/components/SEO'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Users, Award, Lightbulb, Target } from 'lucide-react'

export default function About() {
  return (
    <>
      <SEO
        title="About WithWords - Our Mission & Team"
        description="Learn about WithWords, our mission to revolutionize semantic SEO and topical authority, and the team behind our innovative platform."
      />
      
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-5xl">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-6">About WithWords</h1>
          <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto mb-12">
            We're on a mission to revolutionize how content creators build topical authority and master semantic SEO.
          </p>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold mb-4">Our Story</h2>
              <p className="text-muted-foreground mb-4">
                WithWords was founded in 2023 by a team of SEO experts, content strategists, and AI engineers who recognized a fundamental gap in the content creation ecosystem.
              </p>
              <p className="text-muted-foreground mb-4">
                While traditional SEO focused heavily on backlinks and technical optimizations, we saw the growing importance of semantic relationships and topical authority in search rankings.
              </p>
              <p className="text-muted-foreground">
                We built WithWords to help content creators leverage the power of semantic SEO without needing a deep technical background in natural language processing or vector embeddings.
              </p>
            </div>
            <div className="bg-muted rounded-lg p-8">
              <h3 className="text-xl font-semibold mb-4">Our Core Values</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="mr-3 mt-1 bg-primary/10 p-1 rounded">
                    <Lightbulb className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span className="font-medium">Innovation</span>
                    <p className="text-sm text-muted-foreground">We constantly push the boundaries of what's possible in semantic content analysis.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="mr-3 mt-1 bg-primary/10 p-1 rounded">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span className="font-medium">Accessibility</span>
                    <p className="text-sm text-muted-foreground">We make advanced SEO techniques accessible to everyone, regardless of technical background.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="mr-3 mt-1 bg-primary/10 p-1 rounded">
                    <Award className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span className="font-medium">Quality</span>
                    <p className="text-sm text-muted-foreground">We believe in creating high-quality, valuable content that serves users first.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="mr-3 mt-1 bg-primary/10 p-1 rounded">
                    <Target className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span className="font-medium">Results-Driven</span>
                    <p className="text-sm text-muted-foreground">We focus on delivering measurable improvements in search visibility and traffic.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Mission Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto max-w-5xl px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Our Mission</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-3">Empower Content Creators</h3>
                <p className="text-muted-foreground">
                  We provide tools and insights that empower content creators to build comprehensive content strategies based on semantic relationships.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-3">Elevate Content Quality</h3>
                <p className="text-muted-foreground">
                  We help creators focus on comprehensive, valuable content that satisfies user intent and builds genuine topical authority.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-3">Democratize Advanced SEO</h3>
                <p className="text-muted-foreground">
                  We make advanced semantic SEO techniques accessible to everyone, not just large companies with dedicated SEO teams.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary/5 px-4">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Content Strategy?</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Join thousands of content creators who are already using WithWords to build topical authority and improve their search rankings.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg">
              <Link to="https://app.withwords.app/signup/">Get Started</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}